// "Meditations" — public-site section. Pulls up to N gallery pancakes where
// publicGallery === true and surfaces 3 at a time using weighted random
// sampling, pinned pieces first. Transparent cutouts with drop shadows;
// caption uses the meditation text when written, otherwise falls back to
// subject / recipient name.
import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../../../firebase";
import { sampleMeditations, shortLabel } from "../../../utils/meditations";
import PanBar from "../components/PanBar";

// Reads the PII-safe summary doc maintained by the `syncPublicMeditations`
// Cloud Function. Previously this queried `orders where publicGallery == true`
// directly, which requires auth — meaning anonymous public visitors (including
// in-app browsers like Facebook Messenger) saw nothing. The config doc is
// publicly readable and scales to one doc read per visit.

// Map the config-doc shape back to the shape the rest of the file expects.
function normalizeItems(items) {
  if (!Array.isArray(items)) return [];
  return items.map((i) => ({
    id: i.id,
    type:              i.type || "pancake", // "shift" = griddle meditation on a whole day
    meditation:        i.meditation,
    subjectName:       i.subjectName,
    recipientName:     i.subjectName,  // fallback naming
    artistName:        i.artistName,
    ai_description:    i.aiDescription,
    photo_cutout_url:  i.photoCutoutUrl,
    photo_url:         i.photoUrl,
    collage:           i.collage || [],
    meditationPinned:  i.meditationPinned,
    // Minimal Timestamp-like shims for sampleMeditations' score helpers.
    meditatedAt:  i.meditatedAtMs ? { toMillis: () => i.meditatedAtMs } : null,
    completedAt:  i.completedAtMs ? { toMillis: () => i.completedAtMs } : null,
    createdAt:    i.createdAtMs   ? { toMillis: () => i.createdAtMs   } : null,
  }));
}

export default function TheWork({ navigate }) {
  const [pool, setPool]     = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [shuffleKey, setShuffleKey] = useState(0);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config/publicMeditations"),
      (snap) => {
        if (!snap.exists()) { setPool([]); setLoaded(true); return; }
        setPool(normalizeItems(snap.data()?.items));
        setLoaded(true);
      },
      (err) => {
        console.warn("Meditations config read failed:", err.message);
        setLoaded(true);
      }
    );
    return unsub;
  }, []);

  // Shift meditations rotate with the pancake ones, but at most ONE shift
  // collage card per sample — the section stays pancake-forward.
  const sample = useMemo(() => {
    const picked = sampleMeditations(pool, 3);
    const shiftPicks = picked.filter((p) => p.type === "shift");
    if (shiftPicks.length <= 1) return picked;
    const kept = picked.filter((p) => p.type !== "shift" || p === shiftPicks[0]);
    const fillers = sampleMeditations(
      pool.filter((p) => p.type !== "shift" && !kept.includes(p)),
      3 - kept.length
    );
    return [...kept, ...fillers];
  }, [pool, shuffleKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section
      id="work"
      style={{
        background: "radial-gradient(ellipse 80% 70% at 85% 20%, rgba(239,83,126,0.14) 0%, transparent 55%), radial-gradient(ellipse 70% 60% at 10% 90%, rgba(74,164,225,0.12) 0%, transparent 55%), #0e2e46",
        padding: "5rem 1.25rem",
      }}
    >
      <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
        <PanBar width={80} height={3} margin="0 auto 1.2rem" />
        <h2 style={{
          margin: "0 0 0.6rem",
          fontFamily: "var(--jop-script)",
          fontSize: "clamp(2rem, 5vw, 3.2rem)",
          color: "var(--jop-gold)",
          fontWeight: 700,
          letterSpacing: "-0.01em",
          textAlign: "center",
        }}>
          Meditations.
        </h2>
        <p style={{
          margin: "0 auto 2.5rem", maxWidth: "520px", textAlign: "center",
          color: "rgba(255,218,173,0.65)", fontFamily: "var(--jop-display)",
          fontSize: "0.95rem", lineHeight: 1.55,
        }}>
          A few pancakes worth sitting with.
        </p>

        {!loaded && (
          <div style={{ color: "rgba(255,218,173,0.4)", textAlign: "center", padding: "3rem", fontFamily: "var(--jop-display)", fontSize: "0.9rem" }}>
            Loading…
          </div>
        )}

        {loaded && sample.length === 0 && (
          <div style={{
            maxWidth: "540px", margin: "0 auto",
            border: "1px dashed rgba(252,179,31,0.35)",
            borderRadius: "12px", padding: "1.4rem",
            color: "rgba(255,218,173,0.7)",
            fontFamily: "var(--jop-display)",
            textAlign: "center",
            fontSize: "0.9rem",
            lineHeight: 1.5,
          }}>
            No meditations published yet.
          </div>
        )}

        {loaded && sample.length > 0 && (
          <>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "2rem",
              alignItems: "start",
            }}>
              {sample.map((item) => <MeditationCard key={item.id} item={item} />)}
            </div>

            <div style={{ textAlign: "center", marginTop: "2.5rem", display: "flex", gap: "0.7rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button
                onClick={() => setShuffleKey((k) => k + 1)}
                style={{
                  background: "rgba(239,83,126,0.12)",
                  color: "var(--jop-pink)",
                  border: "1px solid rgba(239,83,126,0.55)",
                  borderRadius: "999px",
                  padding: "0.55rem 1.25rem",
                  fontFamily: "var(--jop-display)",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  letterSpacing: "0.02em",
                  boxShadow: "0 2px 14px rgba(239,83,126,0.2)",
                }}
              >
                ↻ Shuffle
              </button>
              <a
                href="/m"
                style={{
                  background: "transparent",
                  color: "var(--jop-gold)",
                  border: "1px solid rgba(252,179,31,0.5)",
                  borderRadius: "999px",
                  padding: "0.55rem 1.25rem",
                  fontFamily: "var(--jop-display)",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  letterSpacing: "0.02em",
                  textDecoration: "none",
                }}
              >
                View all meditations →
              </a>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// Card on the homepage. Truncates long meditations to ~140 chars with a fade
// + "Read meditation →" link to the canonical /m/{id} permalink. Pancakes
// without a meditation just show the script-font name (no link).
const PREVIEW_LIMIT = 140;

function MeditationCard({ item }) {
  const isShift = item.type === "shift";
  const img = item.photo_cutout_url || item.photo_url;
  const name = isShift ? (item.subjectName || "A day at the griddle") : shortLabel(item);
  const fullText = item.meditation?.trim() || "";
  const hasMeditation = fullText.length > 0;
  const isLong = fullText.length > PREVIEW_LIMIT;
  const preview = isLong
    ? fullText.slice(0, PREVIEW_LIMIT).replace(/\s+\S*$/, "") + "…"
    : fullText;
  const isCutout = !!item.photo_cutout_url;
  const detailHref = isShift ? `/m/shift/${item.id}` : `/m/${item.id}`;
  const collage = isShift ? (item.collage || []).slice(0, 4) : [];

  return (
    <figure style={{
      margin: 0,
      display: "flex", flexDirection: "column",
      alignItems: "center",
      textAlign: "center",
      padding: "0.5rem",
    }}>
      {/* Shift cards fill the image slot with a clustered collage of the day's
          pancakes — same square footprint + drop shadows as a single cutout,
          so the two card types cohere in the grid. */}
      {isShift && collage.length > 0 && (
        <a href={detailHref} style={{ display: "block", width: "100%", cursor: "pointer" }}>
          <div style={{
            width: "100%", aspectRatio: "1 / 1",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            alignItems: "center", justifyItems: "center",
            padding: "0.6rem",
            gap: "0.3rem",
            marginBottom: "0.9rem",
          }}>
            {collage.map((p, i) => (
              <img
                key={p.orderId || i}
                src={p.thumbUrl}
                alt={p.title || "A pancake"}
                loading="lazy"
                style={{
                  maxWidth: "100%", maxHeight: "100%",
                  objectFit: "contain",
                  filter: "drop-shadow(0 10px 20px rgba(0,0,0,0.55)) drop-shadow(0 3px 6px rgba(0,0,0,0.3))",
                  transform: `rotate(${((i * 7) % 9) - 4}deg)`,
                }}
              />
            ))}
          </div>
        </a>
      )}
      {!isShift && img && (
        <a href={hasMeditation ? detailHref : undefined}
           style={{ display: "block", width: "100%", cursor: hasMeditation ? "pointer" : "default" }}>
          <div style={{
            width: "100%", aspectRatio: "1 / 1",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0.6rem",
            marginBottom: "0.9rem",
          }}>
            <img
              src={img}
              alt={name}
              loading="lazy"
              style={{
                maxWidth: "100%", maxHeight: "100%",
                objectFit: "contain",
                filter: isCutout
                  ? "drop-shadow(0 18px 36px rgba(0,0,0,0.6)) drop-shadow(0 4px 10px rgba(0,0,0,0.35))"
                  : "drop-shadow(0 18px 36px rgba(0,0,0,0.5))",
                borderRadius: isCutout ? 0 : "10px",
              }}
            />
          </div>
        </a>
      )}
      <figcaption style={{ padding: "0 0.4rem", maxWidth: "38ch", margin: "0 auto" }}>
        {hasMeditation ? (
          <>
            <p style={{
              margin: 0,
              color: "var(--jop-cream)",
              fontFamily: "var(--jop-display)",
              fontSize: "0.92rem",
              lineHeight: 1.55,
              fontStyle: "italic",
            }}>
              {preview}
            </p>
            {isLong && (
              <a
                href={detailHref}
                style={{
                  display: "inline-block",
                  marginTop: "0.7rem",
                  color: "var(--jop-pink)",
                  background: "rgba(239,83,126,0.1)",
                  border: "1px solid rgba(239,83,126,0.4)",
                  borderRadius: "999px",
                  padding: "0.32rem 0.85rem",
                  fontSize: "0.78rem",
                  fontWeight: 700,
                  textDecoration: "none",
                  letterSpacing: "0.02em",
                }}
              >
                Read meditation →
              </a>
            )}
            {name && (
              <div style={{
                marginTop: "0.55rem",
                color: "rgba(255,218,173,0.5)",
                fontFamily: "var(--jop-display)",
                fontSize: "0.72rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}>
                {isShift ? `🔥 From the griddle · ${name}` : name}
              </div>
            )}
          </>
        ) : (
          <p style={{
            margin: 0,
            color: "var(--jop-cream)",
            fontFamily: "var(--jop-script)",
            fontSize: "1.4rem",
            fontWeight: 700,
            lineHeight: 1.2,
          }}>
            {name}
          </p>
        )}
      </figcaption>
    </figure>
  );
}
